import { calculateDefaultRecurrenceBuffer } from '@frontend/api-client';
import type { RecurrenceInterval } from '@frontend/api-types';
import { dayjs, safeParseInt } from '@frontend/duck-tape';
import { useEffect, useMemo, useState } from '@frontend/react';
import {
  Button,
  DateInput,
  Divider,
  Icon,
  NumberInput,
  SelectInput,
  Text,
  XStack,
  YStack,
  useShortcut,
} from '@frontend/web-react';
import { concatClasses } from '@frontend/web-utils';
import { useAnalytics } from '@frontend/web/hooks';
import { useCreateTaskContext } from '@frontend/web/hooks/context/useCreateTaskContext';
import pluralize from 'pluralize';
import { initialQuestionsInputClassName } from './InitialQuestions';
import { TaskPreview } from './helpers/TaskPreview';

type RecurrenceIntervalOption = {
  label: string;
  value: RecurrenceInterval;
};

export const RecurrenceSettings = () => {
  const {
    closeCreateTaskModal,
    createTaskState,
    decrementStep,
    incrementStep,
    isSubmitting,
    openCreateTaskModal,
    updateTaskData,
  } = useCreateTaskContext();
  useCreateTaskContext();
  const { buffer, interval, intervalCount, nextStartDate } = createTaskState.taskData;
  const [isStepComplete, setIsStepComplete] = useState(
    !!nextStartDate && !!intervalCount && intervalCount > 0 && !!buffer,
  );
  const isSubmitDisabled = !isStepComplete;

  const { track } = useAnalytics();

  const onClickNonRecurringTask = () => {
    track({
      data: {
        messageIdx: createTaskState.taskData.researchThreadReferenceData?.messageIdx,
        researchThreadId: createTaskState.taskData.researchThreadId!,
        suggestion: createTaskState.taskData.researchThreadReferenceData?.suggestion,
        taskSuggestionIdx: createTaskState.taskData.researchThreadReferenceData?.taskSuggestionIdx,
      },
      event: 'taskSuggestionDoesNotRepeat',
      topic: 'research',
    });
    // Intentionally not resetting the onCreateCallback here since we still want to refetch threads data
    openCreateTaskModal({
      initialData: createTaskState.taskData,
      isRecurrence: false,
      stepsToSkip: ['description'],
    });
  };

  useEffect(() => {
    setIsStepComplete(!!nextStartDate);
  }, [nextStartDate]);

  useShortcut({
    Enter: {
      isDisabled: isSubmitDisabled,
      onSubmit: incrementStep,
    },
  });

  useEffect(() => {
    setIsStepComplete(Boolean(!!nextStartDate && !!intervalCount && intervalCount > 0 && !!buffer && interval));
  }, [interval, intervalCount, buffer]);

  const intervalOptions: RecurrenceIntervalOption[] = useMemo(
    () => [
      { label: pluralize('week', intervalCount), value: 'week' },
      { label: pluralize('month', intervalCount), value: 'month' },
      { label: pluralize('year', intervalCount), value: 'year' },
    ],
    [intervalCount],
  );

  return (
    <YStack>
      <XStack className="p-md" justifyContent="center">
        {createTaskState.currentStep !== createTaskState.steps[0] ? (
          <Icon name="IconArrowLeft" onClick={decrementStep} size="lg" />
        ) : null}
        <XStack alignItems="center" className="flex-1" justifyContent="center">
          <Text color="secondary" textAlign="center" type="h5">
            Send to Copilot
          </Text>
        </XStack>
        <Icon name="IconX" onClick={closeCreateTaskModal} size="lg" />
      </XStack>
      <Divider />
      <YStack className="px-md py-md" gapY="md">
        <TaskPreview />
        <Text type="h4">How often should this task repeat?</Text>

        <XStack alignItems="center" gapX="sm">
          <Text type="p-sm">Repeat every</Text>
          <NumberInput
            classNames={{
              input: concatClasses(initialQuestionsInputClassName, 'w-[84px]'),
            }}
            hideControls
            max={365}
            min={1}
            onChange={(intervalCount) =>
              updateTaskData({
                buffer: calculateDefaultRecurrenceBuffer(interval, safeParseInt(intervalCount as string)),
                intervalCount: parseInt(intervalCount as string),
              })
            }
            value={intervalCount}
          />
          <SelectInput
            inputClassName={concatClasses(initialQuestionsInputClassName, 'w-[124px]')}
            name="interval"
            onChange={(interval) => {
              updateTaskData({ buffer: calculateDefaultRecurrenceBuffer(interval, intervalCount), interval });
            }}
            options={intervalOptions}
            value={interval}
          />
        </XStack>
        <XStack alignItems="center" gapX="sm">
          <Text type="p-sm">Starting</Text>
          <DateInput
            inputClassName={concatClasses(initialQuestionsInputClassName)}
            leftSection={<Icon color="tertiary" name="IconCalendar" size={18} />}
            minDate={dayjs().add(1, 'day').toDate()}
            onChange={(nextStartDate: DateISOString | undefined) => {
              updateTaskData({ nextStartDate });
            }}
            placeholder="Start date"
            type="default"
            value={nextStartDate}
            valueFormat="ddd, MMM D, YYYY"
          />
        </XStack>
        <YStack gapY="sm">
          <XStack alignItems="center" gapX="sm">
            <Text type="p-sm">Start task</Text>
            <NumberInput
              classNames={{
                input: concatClasses(initialQuestionsInputClassName, 'w-[100px]'),
              }}
              hideControls
              max={365}
              min={1}
              onChange={(buffer) => {
                updateTaskData({ buffer: safeParseInt(buffer as string) });
              }}
              value={buffer}
            />
            <Text type="p-sm">{pluralize('days', safeParseInt(buffer))} before</Text>
          </XStack>
          {nextStartDate ? (
            <Text color="tertiary" type="caption">
              This will help Copilots ensure that tasks are completed on time.
            </Text>
          ) : null}
        </YStack>
      </YStack>
      <Divider />
      <YStack className="p-md flex-1">
        <Button
          color="primary"
          disabled={isSubmitDisabled}
          fullWidth
          isLoading={isSubmitting}
          label="Proceed with task"
          noBorder
          onClick={incrementStep}
        />
        {/* We only render this button if dialog was opened via thread suggestion */}
        {createTaskState.taskData.researchThreadId ? (
          <Button
            fullWidth
            label="This task doesn't repeat"
            noBorder
            onClick={onClickNonRecurringTask}
            transparent
            type="subtle"
          />
        ) : null}
      </YStack>
    </YStack>
  );
};
